<template>
  <div class="vendor-preview-list vendor-preview-list--glass">
    <!-- Vendor Details Section -->
    <div class="vendor-preview-details-section vendor-preview-details-section--glass">
      <div
        v-if="!showSideMenu"
        class="vendor-preview-section-header vendor-preview-section-header--glass vendor-preview-section-header--toolbar"
      >
        <div class="vendor-preview-header-left">
          <div class="vendor-preview-show-menu-row">
            <SecondaryButton
              label="VendorList"
              icon="bi bi-arrow-left"
              title="Show Vendor List"
              @click="toggleSideMenu"
            />
            <span
              v-if="isAllVendorsMode"
              class="vendor-preview-filter-match-text"
              aria-live="polite"
            >
              {{ trackedVendorsCount }} {{ trackedVendorsCount === 1 ? 'vendor' : 'vendors' }}
            </span>
          </div>
        </div>
      </div>
      <div class="vendor-preview-header vendor-preview-header--glass">
        <span v-if="isIndividualVendorMode && !selectedVendor" class="vendor-preview-no-selection">
          No {{ vendorFilter }} vendor selected. Choose a vendor from the list, or switch to
          <strong>All Vendors</strong> to see combined totals.
        </span>
        <span v-else-if="isAllVendorsMode && trackedVendorsCount === 0" class="vendor-preview-no-selection">
          No vendors match your filters.
        </span>
        <div
          v-else-if="isAllVendorsMode && trackedVendorsCount > 0"
          class="vendor-preview-details-card vendor-preview-details-card--all vendor-preview-details-card--glass"
        >
          <div
            class="vendor-preview-finance-summary vendor-preview-finance-summary--single vendor-preview-finance-summary--glass"
            :class="{ 'vendor-preview-finance-summary--loading': loadingTransactions }"
          >
            <div
              v-if="financialSummaryStatCards.length"
              class="vendor-preview-finance-stat-grid"
              :class="{ 'vendor-preview-finance-stat-grid--loading': loadingTransactions }"
            >
              <div
                v-for="card in financialSummaryStatCards"
                :key="'all-' + card.key"
                class="vendor-preview-finance-stat-card"
                :class="[
                  `vendor-preview-finance-stat-card--${card.tone}`,
                  `vendor-preview-finance-stat-card--layout-${card.layout}`
                ]"
              >
                <div
                  class="vendor-preview-finance-stat-card-icon"
                  :class="`vendor-preview-finance-stat-card-icon--${card.tone}`"
                >
                  <i :class="card.icon" aria-hidden="true"></i>
                </div>
                <div class="vendor-preview-finance-stat-card-content">
                  <span class="vendor-preview-finance-stat-card-title">{{ card.title }}</span>
                  <div
                    class="vendor-preview-finance-stat-card-metrics vendor-preview-finance-stat-card-metrics--primary-cols"
                    :class="{
                      'vendor-preview-finance-stat-card-metrics--two-cols': !card.primary && card.side && card.side.length === 2
                    }"
                  >
                    <div
                      v-if="card.primary"
                      class="vendor-preview-finance-stat-card-metric"
                    >
                      <span class="vendor-preview-finance-stat-card-col-label">{{ card.primary.label }}</span>
                      <span
                        class="vendor-preview-finance-stat-card-col-value"
                        :class="card.primary.valueClass"
                      >{{ card.primary.value }}</span>
                    </div>
                    <div
                      v-for="(item, sideIndex) in card.side"
                      :key="`${card.key}-metric-${sideIndex}`"
                      class="vendor-preview-finance-stat-card-metric"
                    >
                      <span v-if="item.label" class="vendor-preview-finance-stat-card-col-label">{{ item.label }}</span>
                      <span
                        class="vendor-preview-finance-stat-card-col-value"
                        :class="item.valueClass"
                      >{{ item.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="vendor-preview-finance-group vendor-preview-finance-group--glass">
              <div class="vendor-preview-finance-group-title">Vendor bills &amp; pay bills</div>
              <div class="vendor-preview-finance-metrics vendor-preview-finance-metrics--stacked">
                <div
                  v-for="row in financeVendorMetricRows"
                  :key="'all-' + row.key"
                  class="vendor-preview-finance-metric vendor-preview-finance-metric--stack vendor-preview-finance-metric--stack-clickable"
                  :class="{ 'vendor-preview-finance-metric--stack-active': activeFinanceMetricFilterKey === row.key }"
                  role="button"
                  tabindex="0"
                  title="Show matching items in the transactions list"
                  :aria-pressed="activeFinanceMetricFilterKey === row.key"
                  @click="applyFinanceMetricTransactionFilter(row.key)"
                  @keydown.enter.prevent="applyFinanceMetricTransactionFilter(row.key)"
                  @keydown.space.prevent="applyFinanceMetricTransactionFilter(row.key)"
                >
                  <div :class="financeMetricSummaryRowClasses(row.key)">
                    <span class="vendor-preview-finance-metric-stack-label">
                      <span :class="['vendor-preview-finance-metric-stack-icon-wrap', financeMetricStackIconWrapClass(row.key)]">
                        <i :class="financeMetricRowIcon(row.key)" aria-hidden="true"></i>
                      </span>
                      <span class="vendor-preview-finance-metric-stack-label-text">{{ row.label }}</span>
                    </span>
                    <span class="vendor-preview-finance-metric-stack-count">{{ row.count }}</span>
                    <span class="vendor-preview-finance-metric-stack-value">{{ formatCurrency(row.amount) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="isIndividualVendorMode && selectedVendor" class="vendor-preview-details-card vendor-preview-details-card--glass">
          <div
            class="vendor-preview-card-header vendor-preview-card-header--glass"
            :class="{ 'vendor-preview-card-header--summary-expanded': financeSummaryExpanded }"
          >
            <div
              class="vendor-preview-card-title vendor-preview-name-prices vendor-preview-card-title--toggle"
              role="button"
              tabindex="0"
              :aria-expanded="financeSummaryExpanded ? 'true' : 'false'"
              title="Expand or collapse financial summary"
              @click="toggleFinanceSummaryExpanded"
              @keydown.enter.prevent="toggleFinanceSummaryExpanded"
              @keydown.space.prevent="toggleFinanceSummaryExpanded"
            >
              <div class="vendor-preview-card-name">
                {{ selectedVendor.vendorName }} | {{ selectedVendor.vendorId }}
              </div>
              <div class="vendor-preview-card-balance">
                <span class="vendor-preview-balance-label">Balance:</span>
                <span class="vendor-preview-balance-amount">${{ selectedVendor ? (selectedVendor.balance !== undefined ? Number(selectedVendor.balance).toFixed(2) : '0.00') : '0.00' }}</span>
                <button
                  type="button"
                  class="vendor-preview-statement-icon-btn"
                  title="View Statement"
                  aria-label="View Statement"
                  @click.stop="goToStatement"
                >
                  <i class="bi bi-file-text" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <div class="vendor-preview-card-actions vendor-preview-card-actions--inline" @click.stop>
              <SecondaryButton
                label="Create Bill"
                icon="bi bi-file-earmark-text"
                @click="goToAddBill"
              />
              <SecondaryButton
                label="Pay Bill"
                icon="bi bi-cash-coin"
                @click="goToPayBill"
              />
              <SecondaryButton
                icon="bi bi-eye"
                :title="financeSummaryExpanded ? 'Hide summary' : 'Show summary'"
                :aria-expanded="financeSummaryExpanded ? 'true' : 'false'"
                @click="toggleFinanceSummaryExpanded"
              >
                <span>Preview</span>
                <i
                  :class="['bi', financeSummaryExpanded ? 'bi-chevron-up' : 'bi-chevron-down', 'vendor-preview-preview-btn-chevron']"
                  aria-hidden="true"
                />
              </SecondaryButton>
            </div>
          </div>
          <div
            v-show="financeSummaryExpanded"
            class="vendor-preview-finance-summary vendor-preview-finance-summary--single vendor-preview-finance-summary--glass"
            :class="{ 'vendor-preview-finance-summary--loading': loadingTransactions }"
          >
            <div
              v-if="financialSummaryStatCards.length"
              class="vendor-preview-finance-stat-grid"
              :class="{ 'vendor-preview-finance-stat-grid--loading': loadingTransactions }"
            >
              <div
                v-for="card in financialSummaryStatCards"
                :key="card.key"
                class="vendor-preview-finance-stat-card"
                :class="[
                  `vendor-preview-finance-stat-card--${card.tone}`,
                  `vendor-preview-finance-stat-card--layout-${card.layout}`
                ]"
              >
                <div
                  class="vendor-preview-finance-stat-card-icon"
                  :class="`vendor-preview-finance-stat-card-icon--${card.tone}`"
                >
                  <i :class="card.icon" aria-hidden="true"></i>
                </div>
                <div class="vendor-preview-finance-stat-card-content">
                  <span class="vendor-preview-finance-stat-card-title">{{ card.title }}</span>
                  <div
                    class="vendor-preview-finance-stat-card-metrics vendor-preview-finance-stat-card-metrics--primary-cols"
                    :class="{
                      'vendor-preview-finance-stat-card-metrics--two-cols': !card.primary && card.side && card.side.length === 2
                    }"
                  >
                    <div
                      v-if="card.primary"
                      class="vendor-preview-finance-stat-card-metric"
                    >
                      <span class="vendor-preview-finance-stat-card-col-label">{{ card.primary.label }}</span>
                      <span
                        class="vendor-preview-finance-stat-card-col-value"
                        :class="card.primary.valueClass"
                      >{{ card.primary.value }}</span>
                    </div>
                    <div
                      v-for="(item, sideIndex) in card.side"
                      :key="`${card.key}-metric-${sideIndex}`"
                      class="vendor-preview-finance-stat-card-metric"
                    >
                      <span v-if="item.label" class="vendor-preview-finance-stat-card-col-label">{{ item.label }}</span>
                      <span
                        class="vendor-preview-finance-stat-card-col-value"
                        :class="item.valueClass"
                      >{{ item.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="vendor-preview-summary-columns">
              <div class="vendor-preview-finance-group vendor-preview-finance-group--glass">
                <div class="vendor-preview-finance-group-title">Vendor bills &amp; pay bills</div>
                <div class="vendor-preview-finance-metrics vendor-preview-finance-metrics--stacked">
                  <div
                    v-for="row in financeVendorMetricRows"
                    :key="row.key"
                    class="vendor-preview-finance-metric vendor-preview-finance-metric--stack vendor-preview-finance-metric--stack-clickable"
                    :class="{ 'vendor-preview-finance-metric--stack-active': activeFinanceMetricFilterKey === row.key }"
                    role="button"
                    tabindex="0"
                    title="Show matching items in the transactions list"
                    :aria-pressed="activeFinanceMetricFilterKey === row.key"
                    @click="applyFinanceMetricTransactionFilter(row.key)"
                    @keydown.enter.prevent="applyFinanceMetricTransactionFilter(row.key)"
                    @keydown.space.prevent="applyFinanceMetricTransactionFilter(row.key)"
                  >
                    <div :class="financeMetricSummaryRowClasses(row.key)">
                      <span class="vendor-preview-finance-metric-stack-label">
                        <span :class="['vendor-preview-finance-metric-stack-icon-wrap', financeMetricStackIconWrapClass(row.key)]">
                          <i :class="financeMetricRowIcon(row.key)" aria-hidden="true"></i>
                        </span>
                        <span class="vendor-preview-finance-metric-stack-label-text">{{ row.label }}</span>
                      </span>
                      <span class="vendor-preview-finance-metric-stack-count">{{ row.count }}</span>
                      <span class="vendor-preview-finance-metric-stack-value">{{ formatCurrency(row.amount) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="vendor-preview-expandable-details vendor-preview-expandable-details--glass">
                <div class="customer-details-cards customer-details-cards--tabbed">
                  <h4 id="vendor-preview-details-heading" class="customer-details-cards__heading">
                    Vendor details
                  </h4>
                  <nav
                    class="vendor-preview-details-tabs"
                    role="tablist"
                    aria-labelledby="vendor-preview-details-heading"
                  >
                    <button
                      v-for="tab in vendorDetailsTabs"
                      :key="tab.id"
                      type="button"
                      role="tab"
                      class="vendor-preview-details-tab"
                      :class="{ 'vendor-preview-details-tab--active': vendorDetailsTab === tab.id }"
                      :aria-selected="vendorDetailsTab === tab.id"
                      @click="vendorDetailsTab = tab.id"
                    >
                      <i
                        v-if="tab.icon"
                        :class="tab.icon"
                        class="vendor-preview-details-tab-icon"
                        aria-hidden="true"
                      ></i>
                      {{ tab.name }}
                    </button>
                  </nav>

                  <div
                    v-show="vendorDetailsTab === 'information'"
                    class="vendor-preview-details-tab-panel"
                    role="tabpanel"
                  >
                    <div class="vendor-preview-details-tab-cards">
                      <CustomerDetailsCard
                        label="Email"
                        icon-class="bi bi-envelope"
                        full-row
                        :value="selectedVendor ? selectedVendor.email : ''"
                      />
                      <CustomerDetailsCard
                        label="Address"
                        icon-class="bi bi-geo-alt"
                        tall
                        full-row
                      >
                        <template v-if="selectedVendor && (selectedVendor.address || selectedVendor.addressDetails)">
                          <div v-if="selectedVendor.address">{{ selectedVendor.address }}</div>
                          <div v-if="selectedVendor.addressDetails">{{ selectedVendor.addressDetails }}</div>
                        </template>
                      </CustomerDetailsCard>
                    </div>
                  </div>

                  <div
                    v-show="vendorDetailsTab === 'notes'"
                    class="vendor-preview-details-tab-panel"
                    role="tabpanel"
                  >
                    <div class="customer-preview-notes-toolbar">
                      <PrimaryButton
                        label="Add Note"
                        icon="bi bi-plus-lg"
                        compact
                        @click="openAddVendorNoteModal"
                      />
                    </div>
                    <div class="vendor-preview-details-tab-cards">
                      <template v-if="hasVendorNotesContent">
                        <CustomerDetailsCard
                          v-if="selectedVendor && selectedVendor.notes"
                          key="vendor-profile-notes"
                          label="Profile Notes"
                          icon-class="bi bi-journal-text"
                          full-row
                          :value="selectedVendor.notes"
                        />
                        <CustomerDetailsCard
                          v-for="note in vendorNotes"
                          :key="`vendor-note-${note.id}`"
                          :label="note.title"
                          icon-class="bi bi-journal-text"
                          variant="note"
                          full-row
                        >
                          <template #headerActions>
                            <SecondaryButton
                              label=""
                              icon="bi bi-pencil"
                              custom-class="customer-preview-note-edit-btn"
                              aria-label="Edit note"
                              title="Edit"
                              @click="openEditVendorNoteModal(note)"
                            />
                          </template>
                          <div class="customer-preview-note-card">
                            <div
                              v-if="note.tags && note.tags.length"
                              class="customer-preview-note-card__toolbar"
                            >
                              <div class="customer-preview-note-tags">
                                <span
                                  v-for="tag in note.tags"
                                  :key="`note-tag-${note.id}-${tag.id}`"
                                  class="customer-preview-note-tag customer-preview-note-tag--vendor customer-preview-note-tag--with-strip"
                                  :style="noteTagStyle(tag)"
                                >
                                  <span
                                    class="customer-preview-note-tag__strip"
                                    :style="{ backgroundColor: tag.color || '#6366f1' }"
                                    aria-hidden="true"
                                  ></span>
                                  {{ tag.name }}
                                </span>
                              </div>
                            </div>
                            <div v-if="note.notes" class="customer-preview-note-body">{{ note.notes }}</div>
                            <div
                              v-if="note.noteType || note.priority || note.followUpDate || note.isPrivate"
                              class="customer-preview-note-meta"
                            >
                              <span
                                v-if="note.noteType"
                                class="customer-preview-note-meta-pill"
                              >
                                <span class="customer-preview-note-meta-pill__label">Type</span>
                                <span class="customer-preview-note-meta-pill__value">{{ note.noteType }}</span>
                              </span>
                              <span
                                v-if="note.priority"
                                class="customer-preview-note-meta-pill"
                                :class="vendorNotePriorityClass(note.priority)"
                              >
                                <span class="customer-preview-note-meta-pill__label">Priority</span>
                                <span class="customer-preview-note-meta-pill__value">{{ note.priority }}</span>
                              </span>
                              <span
                                v-if="note.followUpDate"
                                class="customer-preview-note-meta-pill customer-preview-note-meta-pill--followup"
                              >
                                <span class="customer-preview-note-meta-pill__label">Follow-up</span>
                                <span class="customer-preview-note-meta-pill__value">{{ formatDate(note.followUpDate) }}</span>
                              </span>
                              <span
                                v-if="note.isPrivate"
                                class="customer-preview-note-meta-pill customer-preview-note-meta-pill--private"
                              >
                                <span class="customer-preview-note-meta-pill__label">Visibility</span>
                                <span class="customer-preview-note-meta-pill__value">Private</span>
                              </span>
                            </div>
                            <div
                              v-if="note.attachments && note.attachments.length"
                              class="customer-preview-note-attachments"
                            >
                              <button
                                v-for="attachment in note.attachments"
                                :key="`note-attachment-${attachment.id}`"
                                type="button"
                                class="customer-preview-note-attachment-link"
                                @click="viewVendorNoteAttachment(attachment)"
                              >
                                <i class="bi bi-paperclip" aria-hidden="true"></i>
                                {{ attachment.originalFileName || attachment.fileName }}
                              </button>
                            </div>
                          </div>
                        </CustomerDetailsCard>
                      </template>
                      <p v-else class="customer-preview-notes-empty">
                        No notes yet. Click <strong>Add Note</strong> to create one.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Transactions Section -->
    <div ref="transactionsSection" class="vendor-preview-transactions-section consultant-preview-transactions-section">
      <div class="vendor-preview-section-header consultant-preview-section-header vendor-preview-transactions-section-header">
        <h3 class="vendor-preview-section-title consultant-preview-section-title">Transactions</h3>
      </div>
      <div class="vendor-preview-transactions-filters vendor-preview-transactions-filters-row">
        <LabelDropdown
          label="Current View"
          select-id="vendorTransactionsCurrentViewSelect"
          v-model="localCurrentViewFilter"
          inline
          custom-class="ven-txn-filter--view"
          :options="currentViewOptions"
        />
        <LabelDropdown
          label="Type"
          select-id="vendorTransactionsTypeSelect"
          v-model="localTypeFilter"
          inline
          custom-class="ven-txn-filter--type"
          :options="typeDropdownOptions"
        />
        <LabelDropdown
          label="Date"
          select-id="vendorTransactionsDateSelect"
          v-model="localDateFilter"
          inline
          custom-class="ven-txn-filter--date"
          :options="dateFilterOptions"
        />
        <LabelDateField
          v-if="localDateFilter === 'custom'"
          input-id="vendorTransactionsDateFrom"
          label="From"
          v-model="localCustomFromDate"
          placeholder="MM/DD/YYYY"
          inline
          custom-class="ven-txn-filter--date"
        />
        <LabelDateField
          v-if="localDateFilter === 'custom'"
          input-id="vendorTransactionsDateTo"
          label="To"
          v-model="localCustomToDate"
          placeholder="MM/DD/YYYY"
          inline
          custom-class="ven-txn-filter--date"
        />
        <div class="ven-txn-global-search">
          <label class="ven-txn-global-search__label" for="vendorTransactionsGlobalSearch">Search</label>
          <input
            id="vendorTransactionsGlobalSearch"
            v-model.trim="localTransactionSearchQuery"
            type="text"
            class="ven-txn-global-search__input"
            placeholder="Search no, vendor, memo, status..."
          />
        </div>
        <div class="vendor-preview-export-group">
          <PrimaryButton
            label="Export to CSV"
            icon="bi bi-download"
            @click="handleExport"
          />
        </div>
      </div>
      <div
        class="consultant-preview-table-responsive"
        :class="{ 'consultant-preview-table-responsive--loading': loadingTransactions }"
      >
        <div
          v-if="loadingTransactions"
          class="consultant-preview-transactions-loading-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <i class="bi bi-arrow-clockwise consultant-preview-transactions-loading-spinner" aria-hidden="true"></i>
          <span>Loading transactions...</span>
        </div>
        <div class="consultant-preview-table-scroll-x">
        <table class="consultant-glass-table consultant-preview-transactions-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Vendor</th>
            <th>No</th>
            <th>Memo</th>
            <th>Due Date</th>
            <th>Amount</th>
            <th>Account</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loadingTransactions" key="transactions-loading">
            <td :colspan="transactionsTableColspan" class="consultant-preview-loading" aria-hidden="true">&nbsp;</td>
          </tr>
          <tr v-else-if="!canShowTransactions" key="transactions-no-panel">
            <td :colspan="transactionsTableColspan" class="consultant-preview-no-transactions">
              Select a vendor or switch to <strong>All Vendors</strong> to view transactions.
            </td>
          </tr>
          <tr v-else-if="paginatedTransactions.length === 0" key="transactions-empty">
            <td :colspan="transactionsTableColspan" class="consultant-preview-no-transactions">No transactions found</td>
          </tr>
          <template v-else>
          <tr
            v-for="(transaction, index) in paginatedTransactions"
            :key="getTransactionKey(transaction, index)"
            :class="[
              'consultant-preview-transaction-row',
              { 'consultant-preview-transaction-row--menu-open': isActionMenuOpen(transaction, index) }
            ]"
          >
              <td>{{ formatDate(transaction.date) }}</td>
              <td>
                <span v-if="transaction && transaction.type === 'Pay Bill'">Payment</span>
                <span v-else-if="transaction && transaction.type === 'Bill'">Bill</span>
                <span v-else-if="transaction && transaction.type === 'Cash Purchase'">Cash Purchase</span>
                <span v-else-if="transaction && transaction.type === 'Check'">Check</span>
                <span v-else-if="transaction && transaction.type === 'Vendor Credit Note'">Vendor Credit Note</span>
                <span v-else-if="transaction && transaction.type === 'Expense'">Expense</span>
                <span v-else>{{ (transaction && transaction.type) || 'Transaction' }}</span>
                <span
                  v-if="transaction && transaction.isBankRecorded"
                  class="rp-bank-linked-chip"
                  :title="bankRecordedTitle(transaction.bankRecord)"
                >
                  <i class="bi bi-bank2" aria-hidden="true"></i>
                  Bank Recorded
                </span>
              </td>
              <td>{{ getPartyName(transaction) }}</td>
              <td>{{ transaction.number || '-' }}</td>
              <td>{{ transaction.memo || '-' }}</td>
              <td>{{ transaction.dueDate ? formatDate(transaction.dueDate) : '-' }}</td>
              <td>{{ formatCurrency(transaction.amount) }}</td>
              <td>{{ formatAccount(transaction.account) }}</td>
              <td>
                <span :class="['consultant-preview-status', getStatusClass(transaction && transaction.status)]">
                  {{ (transaction && (transaction.type === 'Pay Bill' || transaction.type === 'Expense')) ? 'Paid' : ((transaction && transaction.status) || '') }}
                </span>
              </td>
              <td class="consultant-preview-transaction-actions-cell">
                <div
                  v-if="hasTransactionDetailsActions(transaction)"
                  class="cpt-row-actions"
                >
                  <button
                    v-if="hasViewableDetails(transaction)"
                    type="button"
                    class="cpt-row-action-btn cpt-row-action-btn--view"
                    :title="isDirectExpenseWithDetails(transaction) ? 'View expense' : 'View'"
                    :aria-label="isDirectExpenseWithDetails(transaction) ? 'View expense' : 'View transaction'"
                    @click.stop="viewTransaction(transaction)"
                  >
                    <i class="bi bi-eye" aria-hidden="true"></i>
                  </button>
                  <div
                    v-if="hasTransactionMenuActions(transaction)"
                    class="cpt-row-action-menu-wrap"
                  >
                    <button
                      type="button"
                      class="cpt-row-action-btn cpt-row-action-btn--more"
                      :class="{ 'cpt-row-action-btn--active': isActionMenuOpen(transaction, index) }"
                      title="More actions"
                      aria-label="More actions"
                      aria-haspopup="menu"
                      :aria-expanded="isActionMenuOpen(transaction, index) ? 'true' : 'false'"
                      @click.stop="toggleActionMenu($event, transaction, index)"
                    >
                      <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                <span v-else>-</span>
              </td>
            </tr>
          </template>
        </tbody>
        </table>
        </div>
      </div>
      <div class="vendor-preview-transactions-pagination consultant-preview-transactions-pagination">
        <!-- Use PaginatorComponent for large datasets (>= 2000 records) - like ConsultantsList -->
        <PaginatorComponent
          v-if="(filteredTransactions && filteredTransactions.length) >= 2000"
          key="vendor-transactions-paginator"
          :current-page="currentPage || 1"
          :total-count="(filteredTransactions && filteredTransactions.length) || 0"
          :records-per-page="itemsPerPage || 10"
          :loading="loadingTransactions"
          @page-changed="handlePageChange"
        />
        <!-- Use PaginationComponent for smaller datasets (< 2000 records) -->
        <PaginationComponent
          v-else
          key="vendor-transactions-pagination"
          :current-page="currentPage || 1"
          :total-items="(filteredTransactions && filteredTransactions.length) || 0"
          :items-per-page="itemsPerPage || 10"
          item-label="transactions"
          @page-change="handlePageChange"
          @items-per-page-change="handleItemsPerPageChange"
        />
      </div>
    </div>

    <BillDetailsModal
      :show="showBillDetailsModal"
      :enter-bill-id="billDetailsModalEnterBillId"
      :bill-title="billDetailsModalTitle"
      :vendor-name="billDetailsModalVendor"
      :consultant-record-status="billDetailsModalBillStatus"
      :company-id="billDetailsModalCompanyId"
      @close="closeBillDetailsModal"
    />

    <PayBillDetailsModal
      :show="showPayBillDetailsModal"
      :company-id="payBillDetailsModalCompanyId"
      :vendor-pay-bill-id="payBillDetailsModalVendorPayBillId"
      :pay-bill-no="payBillDetailsModalPayBillNo"
      :vendor-name="payBillDetailsModalVendorName"
      :highlight-pay-bill-record-ids="payBillDetailsModalHighlightPayBillRecordIds"
      @close="closePayBillDetailsModal"
    />

    <VendorDirectExpenseDetailsModal
      :show="showDirectExpenseDetailsModal"
      :expense-id="directExpenseDetailsModalId"
      :expense-kind="directExpenseDetailsModalKind"
      :expense-no="directExpenseDetailsModalNo"
      :vendor-name="directExpenseDetailsModalVendorName"
      :company-id="directExpenseDetailsModalCompanyId"
      :accounts-map="accountsMap"
      @close="closeDirectExpenseDetailsModal"
    />

    <TransactionAuditLog
      :show="showTransactionAuditLog"
      :transaction="auditLogTransaction"
      :company-id="auditLogCompanyId"
      :consultant-name="auditLogPartyName"
      @close="closeTransactionAuditLog"
    />

    <TransactionSummaryDialog
      :show="!!summaryTransaction"
      :title="summaryTitle(summaryTransaction)"
      :bank-recorded="!!summaryTransaction?.isBankRecorded"
      :bank-recorded-title="summaryTransaction?.isBankRecorded ? bankRecordedTitle(summaryTransaction.bankRecord) : ''"
      party-label="Vendor"
      :party-name="summaryPartyName(summaryTransaction)"
      :date-label="summaryTransaction?.type === 'Bill' ? 'Bill Date' : 'Date'"
      :date-text="summaryTransaction?.date ? formatDate(summaryTransaction.date) : ''"
      :due-date-text="summaryTransaction?.dueDate ? formatDate(summaryTransaction.dueDate) : ''"
      :total-label="summaryTransaction?.type === 'Bill' ? 'Bill Total' : 'Total'"
      :total-text="formatCurrency(getTransactionAmountSummary(summaryTransaction).total)"
      :paid-text="formatCurrency(getTransactionAmountSummary(summaryTransaction).paid)"
      :balance-text="formatCurrency(getTransactionAmountSummary(summaryTransaction).balance)"
      :can-download-pdf="!!summaryTransaction && canDownloadTransactionPdf(summaryTransaction)"
      :bill-id="summaryBillId(summaryTransaction)"
      :company-id="companyId"
      :format-currency-fn="formatCurrency"
      :format-date-fn="formatDate"
      @send="onSummarySend"
      @action="onSummaryAction"
      @close="closeTransactionSummary"    >
      <template #badges>
        <span
          v-if="summaryTransaction"
          :class="['consultant-preview-status', getStatusClass(summaryTransaction.status)]"
        >
          {{ summaryStatusLabel(summaryTransaction) }}
        </span>
      </template>
      <template #status>
        <span
          v-if="summaryTransaction"
          :class="['consultant-preview-status', getStatusClass(summaryTransaction.status)]"
        >
          {{ summaryStatusLabel(summaryTransaction) }}
        </span>
      </template>
    </TransactionSummaryDialog>

    <AddVendorNoteModal
      v-if="showAddVendorNoteModal"
      visible
      :vendor-id="selectedVendor?.id"
      :vendor-name="selectedVendor?.vendorName"
      :company-id="companyId"
      :editing-note="editingVendorNote"
      @close="closeAddVendorNoteModal"
      @cancel="closeAddVendorNoteModal"
      @saved="handleVendorNoteSaved"
    />

    <Teleport to="body">
      <div
        v-if="openActionMenuKey && actionMenuTransaction"
        ref="transactionActionMenu"
        class="cpt-row-action-menu cpt-row-action-menu--teleport"
        :class="{ 'cpt-row-action-menu--drop-up': actionMenuPlacement === 'above' }"
        :style="actionMenuStyle"
        role="menu"
        aria-label="Transaction actions"
        @click.stop
        @mousedown.stop
      >
        <div class="cpt-row-action-menu-header">
          <button
            type="button"
            class="cpt-row-action-menu-close"
            aria-label="Close menu"
            @click.stop="closeActionMenu"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>
        <button
          v-for="item in getTransactionMenuItems(actionMenuTransaction)"
          :key="item.id"
          type="button"
          class="cpt-row-action-menu-item"
          :data-action="item.id"
          role="menuitem"
          @click.stop="runTransactionMenuAction(item.id, actionMenuTransaction)"
        >
          <i :class="item.icon" aria-hidden="true"></i>
          <span>{{ item.label }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
import PaginationComponent from '@/components/PaginationComponent.vue';
import PaginatorComponent from '@/components/PaginatorComponent.vue';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service.js';
import { formatBankRecordedTitle } from '@/utils/bankRecordBadge';
import BillDetailsModal from '@/components/BillDetailsModal.vue';
import PayBillDetailsModal from '@/components/PayBillDetailsModal.vue';
import TransactionAuditLogModal from '@/modules/consultant/components/TransactionAuditLog.vue';
import TransactionSummaryDialog from '@/components/TransactionSummaryDialog.vue';
import {
  VENDOR_FINANCE_METRIC_FILTER_MAP,
  buildVendorFinancialSummary,
  buildVendorFinanceMetricRows
} from '../utils/vendorFinanceSummary.js';
import {
  filterVendorBillsByType,
  filterVendorPayBillsByMethod
} from '../utils/vendorBillTypeFilter.js';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import CustomerDetailsCard from '@/modules/customer/components/CustomerDetailsCard.vue';
import AddVendorNoteModal from '@/modules/vendor/components/AddVendorNoteModal.vue';
import VendorDirectExpenseDetailsModal from '@/modules/vendor/components/VendorDirectExpenseDetailsModal.vue';
import vendorNoteService from '@/services/vendorNote.service.js';

const VENDOR_TRANSACTION_VIEW_OPTIONS = [
  { value: 'all', label: 'All Transactions' },
  { value: 'Bill', label: 'Bills' },
  { value: 'Pay Bill', label: 'Pay Bills' },
  { value: 'Cash Purchase', label: 'Cash Purchases' },
  { value: 'Vendor Credit Note', label: 'Vendor Credit Notes' },
  { value: 'Expense', label: 'Expenses' },
];

export default {
  name: 'VendorPreviewList',
  components: {
    PaginationComponent,
    PaginatorComponent,
    BillDetailsModal,
    PayBillDetailsModal,
    TransactionAuditLog: TransactionAuditLogModal,
    TransactionSummaryDialog,
    LabelDropdown,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
    CustomerDetailsCard,
    AddVendorNoteModal,
    VendorDirectExpenseDetailsModal,
  },
  emits: ['page-change', 'items-per-page-change', 'view-filter-changed', 'date-filter-changed', 'type-filter-changed', 'export-csv', 'toggle-side-menu'],
  props: {
    selectedVendor: {
      type: Object,
      default: null
    },
    vendorFilter: {
      type: String,
      default: 'Active'
    },
    transactions: {
      type: Array,
      default: () => []
    },
    loadingTransactions: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    currentViewFilter: {
      type: String,
      default: 'all'
    },
    dateFilter: {
      type: String,
      default: 'all'
    },
    typeFilter: {
      type: String,
      default: 'all'
    },
    accountsMap: {
      type: Object,
      default: () => ({})
    },
    vendors: {
      type: Array,
      default: () => []
    },
    showSideMenu: {
      type: Boolean,
      default: true
    },
    vendorScope: {
      type: String,
      default: 'individual',
      validator: (v) => ['all', 'individual'].includes(v)
    },
    trackedVendorsCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      financeSummaryExpanded: true,
      localCurrentViewFilter: this.currentViewFilter,
      localDateFilter: this.dateFilter,
      localTypeFilter: this.typeFilter,
      localCustomFromDate: '',
      localCustomToDate: '',
      localTransactionSearchQuery: '',
      activeFinanceMetricFilterKey: null,
      applyingFinanceMetricFilter: false,
      showBillDetailsModal: false,
      billDetailsModalEnterBillId: null,
      billDetailsModalTitle: '',
      billDetailsModalVendor: '',
      billDetailsModalBillStatus: '',
      billDetailsModalCompanyId: null,
      showPayBillDetailsModal: false,
      payBillDetailsModalVendorPayBillId: null,
      payBillDetailsModalPayBillNo: '',
      payBillDetailsModalVendorName: '',
      payBillDetailsModalHighlightPayBillRecordIds: [],
      payBillDetailsModalCompanyId: null,
      showDirectExpenseDetailsModal: false,
      directExpenseDetailsModalId: null,
      directExpenseDetailsModalKind: '',
      directExpenseDetailsModalNo: '',
      directExpenseDetailsModalVendorName: '',
      directExpenseDetailsModalCompanyId: null,
      showTransactionAuditLog: false,
      auditLogTransaction: null,
      summaryTransaction: null,
      auditLogCompanyId: null,
      auditLogPartyName: '',
      billAttachmentsByBillId: {},
      openActionMenuKey: null,
      actionMenuTransaction: null,
      actionMenuStyle: null,
      actionMenuPlacement: 'below',
      actionMenuAnchorEl: null,
      vendorDetailsTab: 'information',
      showAddVendorNoteModal: false,
      editingVendorNote: null,
      vendorNotes: [],
      loadingVendorNotes: false,
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id || this.$route.params.companyId || null;
    },
    hasVendorNotesContent() {
      const profileNotes = this.selectedVendor?.notes;
      return !!(profileNotes && String(profileNotes).trim()) || this.vendorNotes.length > 0;
    },
    vendorDetailsTabs() {
      return [
        { id: 'information', name: 'Vendor Information', icon: 'bi bi-person-lines-fill' },
        { id: 'notes', name: 'Notes', icon: 'bi bi-journal-text' },
      ];
    },
    transactionsTableColspan() {
      return 10;
    },
    isAllVendorsMode() {
      return this.vendorScope === 'all';
    },
    isIndividualVendorMode() {
      return this.vendorScope === 'individual';
    },
    canShowTransactions() {
      return this.isAllVendorsMode || !!this.selectedVendor;
    },
    baseTransactionsForFinance() {
      if (!this.transactions || !Array.isArray(this.transactions)) return [];
      return this.transactions.filter((t) => t != null);
    },
    financeTransactionsForHighlights() {
      let txs = this.baseTransactionsForFinance;
      if (this.localDateFilter && this.localDateFilter !== 'all') {
        txs = this.filterByDate(
          txs,
          this.localDateFilter,
          this.localCustomFromDate,
          this.localCustomToDate
        );
      }
      return txs;
    },
    vendorFinancialSummary() {
      return buildVendorFinancialSummary(this.financeTransactionsForHighlights);
    },
    financeVendorMetricRows() {
      return buildVendorFinanceMetricRows(this.vendorFinancialSummary);
    },
    financialSummaryAtAGlance() {
      const summary = this.vendorFinancialSummary;
      const balanceAmount = summary?.outstandingVendor ?? 0;
      const txs = this.financeTransactionsForHighlights;
      let lastBillDate = null;
      let lastBillAmount = null;
      let lastBillTs = -1;
      let lastPayBillDate = null;
      let lastPayBillAmount = null;
      let lastPayBillTs = -1;

      const resolveTxTimestamp = (t) => {
        const tsRaw = Number(t.dateTimestamp);
        if (Number.isFinite(tsRaw) && tsRaw > 0) return tsRaw;
        if (!t.date) return 0;
        const parsed = new Date(t.date).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
      };

      for (const t of txs) {
        if (!t) continue;
        const type = String(t.type || '');
        const ts = resolveTxTimestamp(t);
        if (!Number.isFinite(ts) || ts <= 0) continue;

        if (type === 'Bill' && ts > lastBillTs) {
          lastBillTs = ts;
          lastBillDate = t.date || t.billDate || null;
          const amt = Number(t.amount);
          lastBillAmount = Number.isFinite(amt) ? amt : null;
        }

        if (type === 'Pay Bill' && ts > lastPayBillTs) {
          lastPayBillTs = ts;
          lastPayBillDate = t.date || null;
          const amt = Number(t.amount);
          lastPayBillAmount = Number.isFinite(amt) ? amt : null;
        }
      }

      return {
        balanceAmount,
        lastBillDate,
        lastBillAmount,
        lastPayBillDate,
        lastPayBillAmount
      };
    },
    financialSummaryStatCards() {
      const h = this.financialSummaryAtAGlance;
      if (!h) return [];
      const dash = '-';
      return [
        {
          key: 'vendor',
          tone: 'vendor',
          layout: 'primary-cols',
          icon: 'bi bi-building',
          title: 'Last Bill',
          primary: {
            label: 'Due Amount',
            value: this.formatCurrency(h.balanceAmount),
            valueClass: 'vendor-preview-finance-header-value--vendor-due'
          },
          side: [
            {
              label: 'Bill Date',
              value: h.lastBillDate ? this.formatDate(h.lastBillDate) : dash,
              valueClass: ''
            },
            {
              label: 'Bill Total',
              value: h.lastBillAmount != null ? this.formatCurrency(h.lastBillAmount) : dash,
              valueClass: 'vendor-preview-finance-header-value--bill'
            }
          ]
        },
        {
          key: 'ven-payment',
          tone: 'payment',
          layout: 'primary-cols',
          icon: 'bi bi-currency-dollar',
          title: 'Last Pay Bill',
          side: [
            {
              label: 'Pay Bill Date',
              value: h.lastPayBillDate ? this.formatDate(h.lastPayBillDate) : dash,
              valueClass: ''
            },
            {
              label: 'Pay Bill Amount',
              value: h.lastPayBillAmount != null ? this.formatCurrency(h.lastPayBillAmount) : dash,
              valueClass: 'vendor-preview-finance-header-value--payment'
            }
          ]
        }
      ];
    },
    currentViewOptions() {
      return VENDOR_TRANSACTION_VIEW_OPTIONS;
    },
    dateFilterOptions() {
      return [
        { value: 'all', label: 'All' },
        { value: 'this_week', label: 'This week' },
        { value: 'this_month', label: 'This month' },
        { value: 'last_week', label: 'Last week' },
        { value: 'last_month', label: 'Last month' },
        { value: 'this_financial_year', label: 'This financial year' },
        { value: 'last_financial_year', label: 'Last financial year' },
        { value: 'this_financial_quarter', label: 'This financial quarter' },
        { value: 'last_financial_quarter', label: 'Last financial quarter' },
        { value: 'financial_year_to_date', label: 'Financial year to date' },
        { value: 'custom', label: 'Custom' },
      ];
    },
    filteredTransactions() {
      if (!this.transactions || !Array.isArray(this.transactions)) {
        return [];
      }
      
      let txns = [...this.transactions];

      const financeConfig = this.activeFinanceMetricFilterKey
        ? VENDOR_FINANCE_METRIC_FILTER_MAP[this.activeFinanceMetricFilterKey]
        : null;
      if (financeConfig) {
        if (financeConfig.view && financeConfig.view !== 'all') {
          txns = txns.filter((t) => t && t.type === financeConfig.view);
        }
        if (financeConfig.view === 'Bill' && financeConfig.type && financeConfig.type !== 'all') {
          txns = filterVendorBillsByType(txns, financeConfig.type);
        }
      }
      
      // Filter by vendor active/inactive status first
      if (
        (this.vendorFilter === 'Active' || this.vendorFilter === 'Inactive')
        && this.vendorScope !== 'individual'
        && !this.selectedVendor
      ) {
        const activeVendorIds = this.vendors.filter(v => v.active !== false).map(v => String(v.id));
        const inactiveVendorIds = this.vendors.filter(v => v.active === false).map(v => String(v.id));
        const allowedIds = this.vendorFilter === 'Active' ? activeVendorIds : inactiveVendorIds;
        txns = txns.filter(t => t && t.vendorId && allowedIds.includes(String(t.vendorId)));
      }
      
      // Filter by transaction view (only if not 'all')
      if (this.localCurrentViewFilter && this.localCurrentViewFilter !== 'all') {
        txns = txns.filter(t => {
          if (!t || !t.type) return false;
          return t.type === this.localCurrentViewFilter;
        });
      }
      
      // Filter by type (only applies when view is Bill or Pay Bill, and type filter is not 'all')
      if (this.localCurrentViewFilter === 'Bill') {
        txns = filterVendorBillsByType(txns, this.localTypeFilter);
      } else if (this.localCurrentViewFilter === 'Pay Bill') {
        txns = filterVendorPayBillsByMethod(txns, this.localTypeFilter);
      }
      // Note: When view is 'all', type filter should be ignored or reset to 'all'
      // This is handled in the watcher
      
      // Filter by date
      if (this.localDateFilter && this.localDateFilter !== 'all') {
        try {
          txns = this.filterByDate(
            txns,
            this.localDateFilter,
            this.localCustomFromDate,
            this.localCustomToDate
          );
        } catch (error) {
          logger.warn('Error filtering by date:', error);
        }
      }

      if (this.localTransactionSearchQuery) {
        txns = this.filterBySearchQuery(txns, this.localTransactionSearchQuery);
      }
      
      // Sort by date descending with null safety
      txns.sort((a, b) => {
        if (!a || !b) return 0;
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      });
      
      return txns;
    },
    paginatedTransactions() {
      if (!this.filteredTransactions || !Array.isArray(this.filteredTransactions)) {
        return [];
      }
      // Filter out any null/undefined transactions as a safety measure
      const validTransactions = this.filteredTransactions.filter(t => t != null);
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return validTransactions.slice(start, end);
    },
    typeDropdownOptions() {
      if (!this.localCurrentViewFilter) {
        return [{ value: 'all', label: 'All Transactions' }];
      }
      
      if (this.localCurrentViewFilter === 'all') {
        return [
          { value: 'all', label: 'All Transactions' }
        ];
      } else if (this.localCurrentViewFilter === 'Bill') {
        return [
          { value: 'all', label: 'All Bills' },
          { value: 'Draft', label: 'Draft' },
          { value: 'Open', label: 'Open' },
          { value: 'Overdue', label: 'Overdue' },
          { value: 'Paid', label: 'Paid' }
        ];
      } else if (this.localCurrentViewFilter === 'Pay Bill') {
        return [
          { value: 'all', label: 'All Pay Bills' },
          { value: 'Check', label: 'Check' },
          { value: 'Cash', label: 'Cash' },
          { value: 'Credit Card', label: 'Credit Card' },
          { value: 'Bank Transfer', label: 'Bank Transfer' },
          {value:'Direct Deposit', label: 'Direct Deposit'}
        ];
      } else {
        return [
          { value: 'all', label: 'All' }
        ];
      }
    }
  },
  methods: {
    bankRecordedTitle(rec) {
      return formatBankRecordedTitle(rec, this.formatDate, this.formatCurrency);
    },
    openAddVendorNoteModal() {
      if (!this.selectedVendor) return;
      this.editingVendorNote = null;
      this.showAddVendorNoteModal = true;
    },
    openEditVendorNoteModal(note) {
      if (!this.selectedVendor || !note?.id) return;
      this.editingVendorNote = { ...note };
      this.showAddVendorNoteModal = true;
    },
    closeAddVendorNoteModal() {
      this.showAddVendorNoteModal = false;
      this.editingVendorNote = null;
    },
    noteTagStyle(tag) {
      const color = tag?.color || '#6366f1';
      return {
        borderColor: `${color}55`,
        color,
        background: `${color}14`,
      };
    },
    vendorNotePriorityClass(priority) {
      const key = String(priority || '').trim().toLowerCase();
      if (key === 'high' || key === 'urgent') return 'customer-preview-note-meta-pill--priority-high';
      if (key === 'low') return 'customer-preview-note-meta-pill--priority-low';
      return 'customer-preview-note-meta-pill--priority-normal';
    },
    async fetchVendorNotes() {
      const vendorId = this.selectedVendor?.id;
      const companyId = this.companyId;
      if (!vendorId || !companyId) {
        this.vendorNotes = [];
        return;
      }

      this.loadingVendorNotes = true;
      try {
        const response = await vendorNoteService.getVendorNotes(vendorId, companyId);
        this.vendorNotes = response?.data?.data || [];
      } catch (error) {
        logger.error('Failed to fetch vendor notes:', error);
        this.vendorNotes = [];
        this.$toast && this.$toast.error('Failed to load vendor notes');
      } finally {
        this.loadingVendorNotes = false;
      }
    },
    async handleVendorNoteSaved(savedNote) {
      const wasEdit = !!this.editingVendorNote?.id;
      this.closeAddVendorNoteModal();
      await this.fetchVendorNotes();
      this.$toast && this.$toast.success(
        wasEdit ? 'Note updated successfully' : 'Note saved successfully'
      );
      if (!savedNote?.id) {
        logger.warn('Vendor note saved but response was missing note id.');
      }
    },
    async viewVendorNoteAttachment(attachment) {
      const companyId = this.companyId;
      if (!attachment?.id || !companyId) return;

      try {
        const response = await vendorNoteService.viewVendorNoteAttachment(attachment.id, companyId);
        if (!response?.data) {
          this.$toast && this.$toast.warning('Attachment not found');
          return;
        }

        const contentType = response.headers?.['content-type'] || attachment.mimeType || 'application/octet-stream';
        const blob = response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (error) {
        logger.error('Failed to open note attachment:', error);
        this.$toast && this.$toast.error('Failed to open attachment');
      }
    },
    financeMetricToneClass(key) {
      const tones = {
        'ven-billed': 'vendor-preview-finance-metric-stack-row--tone-invoices',
        'ven-paid': 'vendor-preview-finance-metric-stack-row--tone-receipt',
        'ven-pending': 'vendor-preview-finance-metric-stack-row--tone-pending',
        'ven-overdue': 'vendor-preview-finance-metric-stack-row--overdue'
      };
      return tones[key] || '';
    },
    financeMetricRowIcon(key) {
      const icons = {
        'ven-billed': 'bi bi-file-earmark-text',
        'ven-paid': 'bi bi-file-earmark-check',
        'ven-pending': 'bi bi-hourglass-split',
        'ven-overdue': 'bi bi-exclamation-triangle'
      };
      return icons[key] || 'bi bi-circle-fill';
    },
    financeMetricStackIconWrapClass(key) {
      const wraps = {
        'ven-billed': 'vendor-preview-finance-metric-stack-icon-wrap--invoices',
        'ven-paid': 'vendor-preview-finance-metric-stack-icon-wrap--receipt',
        'ven-pending': 'vendor-preview-finance-metric-stack-icon-wrap--pending',
        'ven-overdue': 'vendor-preview-finance-metric-stack-icon-wrap--overdue'
      };
      return wraps[key] || '';
    },
    financeMetricSummaryRowClasses(key) {
      const row = this.financeVendorMetricRows.find((r) => r.key === key);
      const classes = [
        'vendor-preview-finance-metric-stack-row',
        'vendor-preview-finance-metric-stack-row--summary'
      ];
      const tone = this.financeMetricToneClass(key);
      if (tone) classes.push(tone);
      if (row && row.overdue) {
        classes.push('vendor-preview-finance-metric-stack-row--overdue');
      }
      return classes;
    },
    applyFinanceMetricTransactionFilter(metricKey) {
      const config = VENDOR_FINANCE_METRIC_FILTER_MAP[metricKey];
      if (!config) return;
      this.applyingFinanceMetricFilter = true;
      this.activeFinanceMetricFilterKey = metricKey;
      this.localCurrentViewFilter = config.view;
      this.localTypeFilter = config.type || 'all';
      this.$emit('view-filter-changed', config.view);
      this.$emit('type-filter-changed', config.type || 'all');
      this.handlePageChange(1);
      this.$nextTick(() => {
        this.applyingFinanceMetricFilter = false;
        const el = this.$refs.transactionsSection;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    },
    isBillWithDetails(t) {
      if (!t || t.type !== 'Bill') return false;
      const id = t.enterBillId ?? t.id;
      return id != null && id !== '';
    },
    isPayBillWithDetails(t) {
      if (!t || t.type !== 'Pay Bill') return false;
      const id = t.vendorPayBillId ?? t.id;
      return id != null && id !== '';
    },
    isDirectExpenseWithDetails(t) {
      if (!t || t.type !== 'Expense') return false;
      const kind = t.expenseKind || t.raw?.expenseKind;
      if (kind === 'Cash Expense' || kind === 'Credit Card Expense') {
        return t.id != null && t.id !== '';
      }
      if (t.sourceType === 'cash-expense' || t.sourceType === 'credit-card-expense') {
        return t.id != null && t.id !== '';
      }
      return false;
    },
    getDirectExpenseKind(t) {
      if (!t) return '';
      if (t.expenseKind) return t.expenseKind;
      if (t.sourceType === 'cash-expense') return 'Cash Expense';
      if (t.sourceType === 'credit-card-expense') return 'Credit Card Expense';
      return '';
    },
    hasViewableDetails(transaction) {
      return (
        this.isBillWithDetails(transaction)
        || this.isPayBillWithDetails(transaction)
        || this.isDirectExpenseWithDetails(transaction)
      );
    },
    getBillId(transaction) {
      return transaction?.enterBillId ?? transaction?.id ?? null;
    },
    getBillAttachments(transaction) {
      const billId = this.getBillId(transaction);
      if (billId != null && Array.isArray(this.billAttachmentsByBillId[billId])) {
        return this.billAttachmentsByBillId[billId];
      }
      const raw = transaction?.raw || transaction;
      if (Array.isArray(transaction?.billAttachments)) {
        return transaction.billAttachments;
      }
      if (Array.isArray(raw?.billAttachments)) {
        return raw.billAttachments;
      }
      if (Array.isArray(raw?.attachments)) {
        return raw.attachments;
      }
      return [];
    },
    isBillPdfAttachment(att) {
      if (!att) return false;
      if (att.mimeType === 'application/pdf') return true;
      const name = String(att.filePath || att.fileName || '').toLowerCase();
      return name.endsWith('.pdf');
    },
    canDownloadTransactionPdf(transaction) {
      if (!this.isBillWithDetails(transaction)) return false;
      return this.getBillAttachments(transaction).some((att) => this.isBillPdfAttachment(att));
    },
    async prefetchBillAttachments(transaction) {
      if (!this.isBillWithDetails(transaction)) return;
      const billId = this.getBillId(transaction);
      if (billId == null || billId === '') return;
      if (Array.isArray(this.billAttachmentsByBillId[billId])) return;
      try {
        const bill = await ApiService.getEnterBillById(billId);
        const attachments = Array.isArray(bill?.attachments) ? bill.attachments : [];
        this.billAttachmentsByBillId = {
          ...this.billAttachmentsByBillId,
          [billId]: attachments
        };
        if (this.openActionMenuKey) {
          this.$nextTick(() => this.updateActionMenuPosition());
        }
      } catch (error) {
        logger.error('VendorPreviewList: failed to load bill attachments', error);
      }
    },
    handleDocumentClick(event) {
      const target = event?.target;
      if (
        target &&
        typeof target.closest === 'function' &&
        (target.closest('.cpt-row-action-menu') || target.closest('.cpt-row-action-menu-wrap'))
      ) {
        return;
      }
      this.closeActionMenu();
    },
    getActionMenuKey(transaction, index) {
      return this.getTransactionKey(transaction, index);
    },
    isActionMenuOpen(transaction, index) {
      return this.openActionMenuKey === this.getActionMenuKey(transaction, index);
    },
    bindActionMenuListeners() {
      if (this._actionMenuListenersBound) return;
      this._actionMenuListenersBound = true;
      this._onActionMenuReflow = () => {
        if (this.openActionMenuKey) {
          this.updateActionMenuPosition();
        }
      };
      window.addEventListener('resize', this._onActionMenuReflow);
      window.addEventListener('scroll', this._onActionMenuReflow, true);
    },
    unbindActionMenuListeners() {
      if (!this._actionMenuListenersBound) return;
      this._actionMenuListenersBound = false;
      window.removeEventListener('resize', this._onActionMenuReflow);
      window.removeEventListener('scroll', this._onActionMenuReflow, true);
      this._onActionMenuReflow = null;
    },
    updateActionMenuPosition() {
      const anchor = this.actionMenuAnchorEl;
      if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return;

      const rect = anchor.getBoundingClientRect();
      const menuEl = this.$refs.transactionActionMenu;
      const items = this.actionMenuTransaction
        ? this.getTransactionMenuItems(this.actionMenuTransaction)
        : [];
      const menuWidth = menuEl?.offsetWidth || 132;
      const estimatedHeight = items.length * 28 + 30;
      const menuHeight = menuEl?.offsetHeight || Math.min(estimatedHeight, 320);
      const gap = 4;
      const pad = 8;

      let top = rect.bottom + gap;
      let placement = 'below';
      if (top + menuHeight > window.innerHeight - pad) {
        top = rect.top - menuHeight - gap;
        placement = 'above';
      }
      top = Math.max(pad, Math.min(top, window.innerHeight - menuHeight - pad));

      let left = rect.right - menuWidth;
      left = Math.max(pad, Math.min(left, window.innerWidth - menuWidth - pad));

      this.actionMenuPlacement = placement;
      this.actionMenuStyle = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        zIndex: 10050
      };
    },
    toggleActionMenu(event, transaction, index) {
      const key = this.getActionMenuKey(transaction, index);
      if (this.openActionMenuKey === key) {
        this.closeActionMenu();
        return;
      }

      const btn = event?.currentTarget;
      if (!btn || typeof btn.getBoundingClientRect !== 'function') return;

      const wrap = btn.closest?.('.cpt-row-action-menu-wrap') || btn;

      this.openActionMenuKey = key;
      this.actionMenuTransaction = transaction;
      this.actionMenuAnchorEl = wrap;
      this.updateActionMenuPosition();
      this.bindActionMenuListeners();
      this.prefetchBillAttachments(transaction);
      this.$nextTick(() => {
        this.updateActionMenuPosition();
        requestAnimationFrame(() => this.updateActionMenuPosition());
      });
    },
    closeActionMenu() {
      this.openActionMenuKey = null;
      this.actionMenuTransaction = null;
      this.actionMenuStyle = null;
      this.actionMenuPlacement = 'below';
      this.actionMenuAnchorEl = null;
      this.unbindActionMenuListeners();
    },
    hasTransactionMenuActions(transaction) {
      return this.getTransactionMenuItems(transaction).length > 0;
    },
    hasTransactionDetailsActions(transaction) {
      return (
        this.hasViewableDetails(transaction) || this.hasTransactionMenuActions(transaction)
      );
    },
    getTransactionMenuItems(transaction) {
      const items = [];
      if (transaction) {
        items.push({ id: 'view-summary', label: 'View Summary', icon: 'bi bi-card-text' });
      }
      if (this.isDirectExpenseWithDetails(transaction)) {
        items.push({ id: 'view-expense', label: 'View Expense', icon: 'bi bi-eye' });
      }
      if (this.canDownloadTransactionPdf(transaction)) {
        items.push({ id: 'download-pdf', label: 'Download PDF', icon: 'bi bi-download' });
      }
      if (this.hasViewableDetails(transaction)) {
        items.push({ id: 'send-email', label: 'Send Email', icon: 'bi bi-envelope' });
        items.push({ id: 'audit-logs', label: 'Audit Logs', icon: 'bi bi-clock-history' });
      }
      return items;
    },
    viewTransaction(transaction) {
      this.closeActionMenu();
      this.openTransactionDetails(transaction);
    },
    openTransactionDetails(transaction) {
      if (this.isBillWithDetails(transaction)) {
        this.openBillDetailsModal(transaction);
      } else if (this.isPayBillWithDetails(transaction)) {
        this.openPayBillDetailsModal(transaction);
      } else if (this.isDirectExpenseWithDetails(transaction)) {
        this.openDirectExpenseDetailsModal(transaction);
      }
    },
    runTransactionMenuAction(actionId, transaction) {
      this.closeActionMenu();
      switch (actionId) {
        case 'view-summary':
          this.summaryTransaction = transaction || null;
          break;
        case 'view-expense':
          this.openDirectExpenseDetailsModal(transaction);
          break;
        case 'download-pdf':
          this.handleDownloadTransactionPdf(transaction);
          break;
        case 'send-email':
          this.handleSendTransactionEmail(transaction);
          break;
        case 'audit-logs':
          this.openTransactionAuditLog(transaction);
          break;
        default:
          break;
      }
    },
    handleDownloadTransactionPdf(transaction) {
      if (!transaction) return;
      const attachments = this.getBillAttachments(transaction);
      const pdfAttachment = attachments.find((att) => this.isBillPdfAttachment(att));
      if (!pdfAttachment) {
        this.$toast && this.$toast.warning('PDF not found for this bill');
        return;
      }
      try {
        let filename = pdfAttachment.filePath || pdfAttachment.fileName;
        if (filename && filename.includes('/')) {
          filename = filename.split('/').pop();
        }
        if (!filename) {
          this.$toast && this.$toast.warning('PDF not found for this bill');
          return;
        }
        const companyId = this.$route.params.id || this.$route.params.companyId;
        const downloadUrl = companyId
          ? `${process.env.VUE_APP_API_URL}/vendor/documents/${encodeURIComponent(filename)}?companyId=${companyId}`
          : `${process.env.VUE_APP_API_URL}/vendor/documents/${encodeURIComponent(filename)}`;
        window.open(downloadUrl, '_blank');
        this.$toast && this.$toast.success('Opening PDF…');
      } catch (error) {
        logger.error('VendorPreviewList: PDF download failed', error);
        this.$toast && this.$toast.error('Failed to open PDF');
      }
    },
    handleSendTransactionEmail() {
      this.$toast && this.$toast.info('Send email is not available for this transaction yet.');
    },
    toAuditLogTransaction(transaction) {
      if (!transaction) return null;
      const audit = { ...transaction };
      if (audit.type === 'Bill') {
        audit.type = 'Bill to be Paid';
        if (audit.enterBillId == null) {
          audit.enterBillId = audit.id;
        }
        audit.billNo = audit.billNo || audit.number;
      }
      return audit;
    },
    openTransactionAuditLog(transaction) {
      if (!transaction) return;
      this.closeActionMenu();
      this.auditLogTransaction = this.toAuditLogTransaction(transaction);
      this.auditLogCompanyId =
        this.$route.params.id || this.$route.params.companyId || null;
      this.auditLogPartyName =
        this.selectedVendor?.vendorName ||
        this.selectedVendor?.name ||
        transaction.vendorName ||
        this.getPartyName(transaction) ||
        '';
      this.$nextTick(() => {
        this.showTransactionAuditLog = true;
      });
    },
    closeTransactionSummary() {
      this.summaryTransaction = null;
    },
    onSummarySend() {
      this.handleSendTransactionEmail(this.summaryTransaction);
    },
    onSummaryAction(actionId) {
      const transaction = this.summaryTransaction;
      this.closeTransactionSummary();
      this.runTransactionMenuAction(actionId, transaction);
    },    summaryTypeLabel(transaction) {
      if (!transaction) return '';
      return transaction.type === 'Pay Bill' ? 'Payment' : transaction.type || 'Transaction';
    },
    summaryTitle(transaction) {
      if (!transaction) return '';
      const label = this.summaryTypeLabel(transaction);
      return transaction.number ? `${label} #${transaction.number}` : label;
    },
    summaryBillId(transaction) {
      if (!transaction || transaction.type !== 'Bill') return null;
      return transaction.enterBillId ?? transaction.id ?? null;
    },
    summaryStatusLabel(transaction) {
      if (!transaction) return '';
      if (transaction.type === 'Pay Bill' || transaction.type === 'Expense') return 'Paid';
      return transaction.status || '';
    },
    summaryPartyName(transaction) {
      if (!transaction) return '';
      const name = this.getPartyName(transaction);
      return name && name !== '-' ? String(name) : '';
    },
    /** Total / paid / balance for the View Summary popup. Payments and expenses are always fully applied. */
    getTransactionAmountSummary(transaction) {
      const num = (v) => {
        if (v === null || v === undefined || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const total = num(transaction?.amount) ?? 0;
      if (transaction?.type !== 'Bill') {
        return { total, paid: total, balance: 0 };
      }
      const raw = transaction.raw || transaction;
      const balance = num(raw.remainingAmount) ?? num(raw.balanceAmount);
      const paid = num(raw.paidAmount);
      if (paid != null && balance != null) return { total, paid, balance };
      if (balance != null) return { total, paid: Math.max(0, total - balance), balance };
      if (paid != null) return { total, paid, balance: Math.max(0, total - paid) };
      const isPaid = String(transaction.status || '').toLowerCase() === 'paid';
      return { total, paid: isPaid ? total : 0, balance: isPaid ? 0 : total };
    },
    closeTransactionAuditLog() {
      this.showTransactionAuditLog = false;
      this.auditLogTransaction = null;
      this.auditLogCompanyId = null;
      this.auditLogPartyName = '';
    },
    openBillDetailsModal(transaction) {
      if (!this.isBillWithDetails(transaction)) return;
      this.billDetailsModalEnterBillId = transaction.enterBillId ?? transaction.id;
      this.billDetailsModalTitle =
        transaction.billNo || transaction.number || `#${this.billDetailsModalEnterBillId}`;
      this.billDetailsModalVendor = transaction.vendorName || this.getPartyName(transaction) || '';
      this.billDetailsModalBillStatus = transaction.status || '';
      this.billDetailsModalCompanyId =
        this.$route.params.id || this.$route.params.companyId || null;
      this.showBillDetailsModal = true;
    },
    closeBillDetailsModal() {
      this.showBillDetailsModal = false;
      this.billDetailsModalEnterBillId = null;
      this.billDetailsModalTitle = '';
      this.billDetailsModalVendor = '';
      this.billDetailsModalBillStatus = '';
      this.billDetailsModalCompanyId = null;
    },
    openPayBillDetailsModal(transaction) {
      if (!this.isPayBillWithDetails(transaction)) return;
      this.payBillDetailsModalVendorPayBillId = transaction.vendorPayBillId ?? transaction.id;
      this.payBillDetailsModalPayBillNo =
        transaction.payBillNo || transaction.number || '';
      this.payBillDetailsModalVendorName =
        transaction.vendorName || this.getPartyName(transaction) || '';
      this.payBillDetailsModalHighlightPayBillRecordIds = [];
      this.payBillDetailsModalCompanyId =
        this.$route.params.id || this.$route.params.companyId || null;
      this.showPayBillDetailsModal = true;
    },
    closePayBillDetailsModal() {
      this.showPayBillDetailsModal = false;
      this.payBillDetailsModalVendorPayBillId = null;
      this.payBillDetailsModalPayBillNo = '';
      this.payBillDetailsModalVendorName = '';
      this.payBillDetailsModalHighlightPayBillRecordIds = [];
      this.payBillDetailsModalCompanyId = null;
    },
    openDirectExpenseDetailsModal(transaction) {
      if (!this.isDirectExpenseWithDetails(transaction)) return;
      this.directExpenseDetailsModalId = transaction.id;
      this.directExpenseDetailsModalKind = this.getDirectExpenseKind(transaction);
      this.directExpenseDetailsModalNo = transaction.number || '';
      this.directExpenseDetailsModalVendorName =
        transaction.vendorName || this.getPartyName(transaction) || '';
      this.directExpenseDetailsModalCompanyId =
        this.$route.params.id || this.$route.params.companyId || null;
      this.showDirectExpenseDetailsModal = true;
    },
    closeDirectExpenseDetailsModal() {
      this.showDirectExpenseDetailsModal = false;
      this.directExpenseDetailsModalId = null;
      this.directExpenseDetailsModalKind = '';
      this.directExpenseDetailsModalNo = '';
      this.directExpenseDetailsModalVendorName = '';
      this.directExpenseDetailsModalCompanyId = null;
    },
    goToAddBill() {
      const companyId = this.$route.params.id;
      
      if (this.selectedVendor && this.selectedVendor.id) {
        this.$router.push({ 
          name: 'EnterBill', 
          params: { id: companyId },
          query: { 
            prefillVendor: this.selectedVendor.id,
            vendorName: this.selectedVendor.vendorName || 'Vendor'
          }
        });
      } else {
        this.$router.push({ name: 'EnterBill', params: { id: companyId } });
      }
    },
    goToPayBill() {
      const companyId = this.$route.params.id;
      
      if (this.selectedVendor && this.selectedVendor.id) {
        this.$router.push({ 
          name: 'PayBill', 
          params: { id: companyId },
          query: { 
            prefillVendor: this.selectedVendor.id,
            vendorName: this.selectedVendor.vendorName || 'Vendor'
          }
        });
      } else {
        this.$router.push({ name: 'PayBill', params: { id: companyId } });
      }
    },
    /** Called from Vendor Center Anna; same as Create Bill / Pay Bill buttons. */
    applyAnnaBillNav(which) {
      const w = String(which || '').toLowerCase();
      if (w === 'create') {
        this.goToAddBill();
        return { ok: true, action: 'create' };
      }
      if (w === 'pay') {
        this.goToPayBill();
        return { ok: true, action: 'pay' };
      }
      return { ok: false };
    },
    goToStatement() {
      const companyId = this.$route.params.id;
      
      if (this.selectedVendor && this.selectedVendor.id) {
        this.$router.push({ 
          name: 'VendorsStatementReport', 
          params: { id: companyId },
          query: { 
            vendorId: this.selectedVendor.id
          }
        });
      } else {
        this.$router.push({ 
          name: 'VendorsStatementReport', 
          params: { id: companyId }
        });
      }
    },
    toggleFinanceSummaryExpanded() {
      this.financeSummaryExpanded = !this.financeSummaryExpanded;
    },
    toggleDetailsCard() {
      this.toggleFinanceSummaryExpanded();
    },
    /** Called from Vendor Center Anna; returns result for assistant messages. */
    applyAnnaPreviewMode(mode) {
      if (!this.selectedVendor) return { ok: false, open: false };
      const m = String(mode || 'toggle').toLowerCase();
      if (m === 'open' || m === 'show') this.financeSummaryExpanded = true;
      else if (m === 'close' || m === 'hide') this.financeSummaryExpanded = false;
      else this.financeSummaryExpanded = !this.financeSummaryExpanded;
      return { ok: true, open: this.financeSummaryExpanded };
    },
    toggleVendorPreview() {
      if (!this.selectedVendor) return;
      this.toggleFinanceSummaryExpanded();
    },
    toggleSideMenu() {
      this.$emit('toggle-side-menu');
    },
    handlePageChange(page) {
      this.$emit('page-change', page);
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.$emit('items-per-page-change', itemsPerPage);
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const text = String(dateString).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
          const [year, month, day] = text.split('-');
          return `${month}/${day}/${year}`;
        }
        const date = new Date(text);
        if (isNaN(date.getTime())) return text;
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      } catch (error) {
        logger.warn('Error formatting date:', error);
        return '-';
      }
    },
    formatCurrency(amount) {
      if (amount === undefined || amount === null) return '-';
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(amount);
      } catch (error) {
        logger.warn('Error formatting currency:', error);
        return '-';
      }
    },
    formatAccount(accountId) {
      if (!accountId) return '-';
      if (!this.accountsMap || typeof this.accountsMap !== 'object') return accountId;
      
      const acc = this.accountsMap[accountId];
      if (!acc) return accountId || '-';
      
      try {
        return [
          acc.accountNumber,
          acc.accountName,
          acc.accountType
        ].filter(Boolean).join(' - ');
      } catch (error) {
        logger.warn('Error formatting account:', error);
        return accountId || '-';
      }
    },
    parseDateFilterInput(value, endOfDay = false) {
      if (!value) return null;
      const text = String(value).trim();
      let date = null;
      const usMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

      if (usMatch) {
        const [, month, day, year] = usMatch;
        date = new Date(Number(year), Number(month) - 1, Number(day));
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        const [year, month, day] = text.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(text);
      }

      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null;
      }

      if (endOfDay) {
        date.setHours(23, 59, 59, 999);
      } else {
        date.setHours(0, 0, 0, 0);
      }
      return date;
    },
    filterByDate(transactions, dateFilter, customFrom = '', customTo = '') {
      if (!dateFilter || dateFilter === 'all' || !transactions || !Array.isArray(transactions)) {
        return transactions || [];
      }
      
      const now = new Date();
      let startDate, endDate;
      
      if (dateFilter === 'this_week') {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 7);
      } else if (dateFilter === 'last_week') {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      } else if (dateFilter === 'this_month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else if (dateFilter === 'last_month') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      } else if (dateFilter === 'this_financial_year') {
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear() + 1, 0, 0);
      } else if (dateFilter === 'last_financial_year') {
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear(), 0, 0);
      } else if (dateFilter === 'this_financial_quarter') {
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
      } else if (dateFilter === 'last_financial_quarter') {
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), (quarter - 1) * 3, 1);
        endDate = new Date(now.getFullYear(), quarter * 3, 0);
      } else if (dateFilter === 'financial_year_to_date') {
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = now;
      } else if (dateFilter === 'custom') {
        startDate = this.parseDateFilterInput(customFrom, false);
        endDate = this.parseDateFilterInput(customTo, true);
        if (!startDate || !endDate || startDate > endDate) {
          return transactions;
        }
      }

      return transactions.filter(txn => {
        if (!txn || !txn.date) return false;
        try {
          const txnDate = new Date(txn.date);
          return txnDate >= startDate && txnDate <= endDate;
        } catch (error) {
          logger.warn('Error parsing transaction date:', error);
          return false;
        }
      });
    },
    filterBySearchQuery(transactions, query) {
      if (!query || !Array.isArray(transactions) || transactions.length === 0) {
        return transactions;
      }

      const search = String(query).trim().toLowerCase();
      if (!search) return transactions;

      return transactions.filter((t) => {
        if (!t) return false;
        const fields = [
          t.number,
          t.type,
          t.status,
          t.memo,
          t.description,
          t.paymentMethod,
          t.vendorName,
          t.vendorId,
          this.getPartyName(t)
        ];
        return fields.some((value) => String(value || '').toLowerCase().includes(search));
      });
    },
    getStatusClass(status) {
      if (!status) return 'consultant-preview-status-default';
      switch (status.toLowerCase()) {
        case 'paid': return 'consultant-preview-status-paid';
        case 'sent': return 'consultant-preview-status-sent';
        case 'draft': return 'consultant-preview-status-draft';
        case 'open': return 'consultant-preview-status-open';
        case 'overdue': return 'consultant-preview-status-overdue';
        case 'cancelled': return 'consultant-preview-status-cancelled';
        case 'received': return 'consultant-preview-status-paid';
        case 'partial':
        case 'partially_paid':
          return 'consultant-preview-status-partially_paid';
        default: return 'consultant-preview-status-default';
      }
    },
    handleExport() {
      this.$emit('export-csv');
    },
    getTransactionKey(transaction, index) {
      if (!transaction) {
        return `transaction-null-${index}`;
      }
      const type = transaction.type || 'Transaction';
      const kind = transaction.expenseKind ? `-${transaction.expenseKind}` : '';
      if (transaction.id != null) {
        return `transaction-${type}${kind}-${transaction.id}`;
      }
      if (transaction.number) {
        return `transaction-${type}${kind}-${transaction.number}-${index}`;
      }
      return `transaction-${type}-${index}`;
    },
    getPartyName(transaction) {
      if (!transaction || typeof transaction !== 'object') return '-';
      
      try {
        // Get raw transaction data (from VendorCenter normalization if available)
        const rawTransaction = transaction.raw || transaction;
        
        // For vendor transactions, get vendor name from the transaction
        // Check vendor information first
        if (transaction.vendorName || rawTransaction.vendorName) {
          return transaction.vendorName || rawTransaction.vendorName;
        }
        
        // Check vendor object
        const vendor = transaction.vendor || rawTransaction.vendor;
        if (vendor) {
          if (typeof vendor === 'string') {
            return vendor;
          }
          if (vendor && typeof vendor === 'object') {
            if (vendor.vendorName) {
              return vendor.vendorName;
            }
            if (vendor.name) {
              return vendor.name;
            }
          }
        }
        
        // Check vendorId and get from selectedVendor if available
        const vendorId = transaction.vendorId || rawTransaction.vendorId || rawTransaction.vendor_id;
        if (vendorId && this.selectedVendor && String(this.selectedVendor.id) === String(vendorId)) {
          return this.selectedVendor.vendorName || this.selectedVendor.name || '-';
        }
        
        // For certain transaction types, check for customer information (as fallback)
        // For example, if a bill is paid by a customer, we might want to show customer name
        if (transaction.type === 'Pay Bill' || transaction.type === 'Cash Purchase') {
          // Check if transaction has customerName directly
          if (transaction.customerName || rawTransaction.customerName) {
            return transaction.customerName || rawTransaction.customerName;
          }
          
          // Check customer object
          const customer = transaction.customer || rawTransaction.customer;
          if (customer) {
            if (typeof customer === 'string') {
              return customer;
            }
            if (customer && typeof customer === 'object') {
              if (customer.customerName) {
                return customer.customerName;
              }
              if (customer.name) {
                return customer.name;
              }
            }
          }
        }
        
        // Check for any party/entity name fields
        if (transaction.partyName || rawTransaction.partyName) {
          return transaction.partyName || rawTransaction.partyName;
        }
        
        if (transaction.entityName || rawTransaction.entityName) {
          return transaction.entityName || rawTransaction.entityName;
        }
      } catch (error) {
        logger.error('Error getting party name:', error);
        return '-';
      }
      
      return '-';
    }
  },
  watch: {
    selectedVendor: {
      handler() {
        this.activeFinanceMetricFilterKey = null;
        this.vendorDetailsTab = 'information';
        this.fetchVendorNotes();
      },
      immediate: true,
    },
    currentViewFilter: {
      handler(newVal) {
        if (this.localCurrentViewFilter !== newVal) {
          this.localCurrentViewFilter = newVal;
        }
      },
      immediate: true,
    },
    dateFilter: {
      handler(newVal) {
        if (this.localDateFilter !== newVal) {
          this.localDateFilter = newVal;
        }
      },
      immediate: true,
    },
    typeFilter: {
      handler(newVal) {
        if (this.localTypeFilter !== newVal) {
          this.localTypeFilter = newVal;
        }
      },
      immediate: true,
    },
    localCurrentViewFilter(newVal, oldVal) {
      if (!this.applyingFinanceMetricFilter) {
        this.activeFinanceMetricFilterKey = null;
      }
      if (this.currentViewFilter !== newVal) {
        this.$emit('view-filter-changed', newVal);
      }
      // Reset type filter when view changes manually (not via finance metric row)
      if (
        !this.applyingFinanceMetricFilter &&
        (newVal === 'all' || (oldVal && newVal !== oldVal)) &&
        this.localTypeFilter !== 'all'
      ) {
        this.localTypeFilter = 'all';
        if (this.typeFilter !== 'all') {
          this.$emit('type-filter-changed', 'all');
        }
      }
    },
    localDateFilter(newVal) {
      if (this.dateFilter !== newVal) {
        this.$emit('date-filter-changed', newVal);
      }
      if (newVal !== 'custom') {
        this.localCustomFromDate = '';
        this.localCustomToDate = '';
      }
      this.$emit('page-change', 1);
    },
    localCustomFromDate() {
      this.$emit('page-change', 1);
    },
    localCustomToDate() {
      this.$emit('page-change', 1);
    },
    localTransactionSearchQuery() {
      this.$emit('page-change', 1);
    },
    localTypeFilter(newVal) {
      if (!this.applyingFinanceMetricFilter) {
        this.activeFinanceMetricFilterKey = null;
      }
      // If view is 'all' and type filter is set to something other than 'all', reset it
      if (this.localCurrentViewFilter === 'all' && newVal !== 'all') {
        if (this.localTypeFilter !== 'all') {
          this.localTypeFilter = 'all';
        }
        if (this.typeFilter !== 'all') {
          this.$emit('type-filter-changed', 'all');
        }
        return;
      }
      if (this.typeFilter !== newVal) {
        this.$emit('type-filter-changed', newVal);
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    this.unbindActionMenuListeners();
    this.closeActionMenu();
  }
};
</script>

<style scoped>
@import '../styles/VendorPreviewList.css';
@import '../styles/vendor-preview-details-glass.css';
@import '../styles/vendor-preview-finance-summary.css';
@import '../styles/vendor-preview-finance-stat-cards.css';
@import '../styles/vendor-preview-list-glass.css';
@import '@/modules/customer/styles/customer-preview-details-glass.css';
</style>
